import { Controller, useForm } from "react-hook-form";
import { OverlayTriggerStateContext } from "react-aria-components";
import { useContext } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import { Checkbox2 } from "@/ui2/atoms/Checkbox";
import { CheckboxGroup2 } from "@/ui2/molecules/CheckboxGroup";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { isUserPeerOrCM } from "@/utils/authToken";
import { ModalDivider } from "@/ui2/molecules/Modal";
import { Preferences } from "@/core/types";
import { Radio2 } from "@/ui2/atoms/Radio";
import { RadioGroup2 } from "@/ui2/molecules/RadioGroup";

type CalendarPreferencesModalProps = {
  preferences: Preferences;
  updatePreferences: (preferences: Preferences) => Promise<void>;
};

type FormValues = {
  events: string[];
  view: string;
};

const CALENDAR_VIEWS = [
  { label: "Day", value: "DAY" },
  { label: "Week", value: "WEEK" },
];

const CALENDAR_EVENTS = [
  { label: "Scheduled", value: "scheduled" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Rescheduled", value: "rescheduled" },
  { label: "No Show", value: "no_show" },
];

export function CalendarPreferencesModal({
  preferences,
  updatePreferences,
}: CalendarPreferencesModalProps) {
  const state = useContext(OverlayTriggerStateContext);

  const { control, formState, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      events: preferences.calendar?.events ?? [],
      view: preferences.calendar?.view ?? "DAY",
    },
    mode: "onSubmit",
  });

  const onSubmit = async (values: FormValues) => {
    await updatePreferences({
      ...preferences,
      calendar: { ...preferences.calendar, events: values.events, view: values.view },
    });
    state?.close();
  };

  return (
    <form className="tw:flex tw:flex-col tw:gap-4" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="view"
        render={({ field }) => (
          <RadioGroup2 label="Default Calendar View" onChange={field.onChange} value={field.value}>
            {CALENDAR_VIEWS.map((view) => (
              <Radio2
                key={view.value}
                value={view.value}
                {...getAutomationDataAttr(`CalendarView_${view.value}`)}
              >
                {view.label}
              </Radio2>
            ))}
          </RadioGroup2>
        )}
      />

      {!isUserPeerOrCM() && (
        <Controller
          control={control}
          name="events"
          render={({ field }) => (
            <CheckboxGroup2
              label="Events Shown by Default"
              onChange={field.onChange}
              value={field.value}
            >
              {CALENDAR_EVENTS.map((event) => (
                <Checkbox2
                  key={event.value}
                  value={event.value}
                  {...getAutomationDataAttr(`CalendarEvent_${event.value}`)}
                >
                  {event.label}
                </Checkbox2>
              ))}
            </CheckboxGroup2>
          )}
        />
      )}

      <ModalDivider />

      <div className="tw:flex tw:justify-end tw:gap-3">
        <Button2
          onPress={() => state?.close()}
          size="md"
          type="button"
          variant="secondary"
          {...getAutomationDataAttr("PreferencesCancelButton")}
        >
          Cancel
        </Button2>
        <Button2
          isDisabled={formState.isSubmitting}
          isLoading={formState.isSubmitting}
          labelLoading={null}
          size="md"
          type="submit"
          variant="primary"
          {...getAutomationDataAttr("PreferencesSaveButton")}
        >
          Save
        </Button2>
      </div>
    </form>
  );
}
